import React, { useEffect, useState } from "react";
import { StripeError } from "@stripe/stripe-js";
import { capitalize } from "@common/helpers/string";
import classNames from "classnames";
import { PaymentElement } from "@stripe/react-stripe-js";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { useBillingInfo } from "@hooks/useBillingInfo";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Alert } from "@design-system/Alert/Alert";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Loader } from "@design-system/Loader/Loader";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { Collapsible } from "@design-system/Collapsible/Collapsible";
import { StandardModalBody } from "@design-system/Modal/Modal";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { ChildrenProps } from "../../../types";
import { SelectedPaymentMethod } from "./lib";
import styles from "./PlanManagement.module.scss";

interface Props extends ChildrenProps {
    onConfirm(selectedPaymentMethod: SelectedPaymentMethod): Promise<void>;
    isLoading: boolean;
    busy: boolean;
    stripeError: StripeError | null;
    submitText: React.ReactNode;
    header: React.ReactNode;
}

export const ModalWithPaymentSetupContent: React.FC<Props> = ({
    onConfirm,
    isLoading,
    busy,
    stripeError,
    submitText,
    header,
    children,
}) => {
    const { data, isFetched } = useBillingInfo();
    const [paymentMethod, setPaymentMethod] = useState<SelectedPaymentMethod>(
        SelectedPaymentMethod.EXISTING,
    );

    useEffect(() => {
        if (isFetched && !data?.paymentMethod) {
            setPaymentMethod(SelectedPaymentMethod.NEW);
        }
    }, [data, isFetched]);

    let payment: React.ReactNode;

    if (!isFetched) {
        payment = <Loader />;
    } else if (data?.paymentMethod) {
        payment = (
            <RadioGroup<SelectedPaymentMethod>
                name="paymentMethod"
                aria-label="Payment method"
                value={paymentMethod}
                onValueChange={setPaymentMethod}
                className={styles.paymentMethodGroup}
            >
                <Collapsible
                    className={styles.paymentCollapsible}
                    open={false}
                    header={
                        <Radio
                            label={
                                <>
                                    {capitalize(
                                        data.paymentMethod.brand.toLowerCase(),
                                    )}{" "}
                                    {getAnonymousAccountNumber(
                                        data.paymentMethod.last4,
                                        2,
                                        "",
                                    )}
                                </>
                            }
                            value={SelectedPaymentMethod.EXISTING}
                            className={styles.paymentMethod}
                        />
                    }
                    stacked
                />
                <Collapsible
                    className={styles.paymentCollapsible}
                    header={
                        <Radio
                            label="Add payment method"
                            value={SelectedPaymentMethod.NEW}
                            className={styles.paymentMethod}
                        />
                    }
                    stacked
                    last
                    open={paymentMethod === SelectedPaymentMethod.NEW}
                >
                    <PaymentElement />
                </Collapsible>
            </RadioGroup>
        );
    } else {
        payment = <PaymentElement />;
    }

    return (
        <StandardModalBody
            className={classNames(styles.planManagement, {
                [styles.loading]: isLoading,
            })}
        >
            <section className={styles.planManagementLayout}>
                <main className={styles.details}>
                    <header className={styles.header}>{header}</header>

                    <section>{children}</section>
                </main>
                <aside className={styles.payment}>
                    <h3>Payment Details</h3>
                    {stripeError && (
                        <aside className="mb-4">
                            <Alert variant="danger">
                                {stripeError.message}
                            </Alert>
                        </aside>
                    )}

                    <section className="mb-4">{payment}</section>
                    <ButtonWithLoader
                        loading={busy}
                        onClick={() => onConfirm(paymentMethod)}
                        variant="tertiary"
                        size="xl"
                        block
                        data-testid="subscription-submit-button"
                    >
                        {submitText}
                    </ButtonWithLoader>
                </aside>
            </section>

            <aside className={styles.loader}>
                <Loader />
            </aside>
        </StandardModalBody>
    );
};
